import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Truck, Send, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/site/PageHero";
import SEO from "@/components/site/SEO";
import { COMPANY, REQUEST_TYPES } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    nom: "",
    telephone: "",
    email: "",
    type_demande: REQUEST_TYPES[0],
    message: "",
    consentement: false,
  });
  const [sending, setSending] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consentement) {
      toast.error("Merci d'accepter la politique RGPD pour envoyer votre demande.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Demande envoyée. Nous vous recontactons rapidement !");
      setForm({
        nom: "",
        telephone: "",
        email: "",
        type_demande: REQUEST_TYPES[0],
        message: "",
        consentement: false,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de l'envoi. Réessayez ou appelez-nous.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact plombier Colmar Mulhouse Strasbourg | Devis gratuit — RM Plomberie"
        description="Contactez RM Plomberie au 07 67 62 44 36 pour un devis gratuit. Intervention rapide à Colmar, Mulhouse, Strasbourg, Ribeauvillé, Sélestat et tout le Haut-Rhin. Plomberie, chauffage, VMC 7j/7."
        keywords="contact plombier Colmar, devis plombier Mulhouse, urgence plomberie Strasbourg, contact chauffagiste Haut-Rhin, téléphone plombier 68, RM Plomberie contact, plombier proche Colmar"
      />
      <PageHero
        breadcrumb="Contact"
        title="TACT"
        highlight="CON"
        subtitle="Une question, un projet ? Nous sommes à votre écoute. Remplissez le formulaire ou contactez-nous directement, nous vous répondrons rapidement."
        useLogo
      />

      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          {/* Coords */}
          <aside className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-3">Nos coordonnées</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#001B35]">Contactez-nous</h2>
            <div className="mt-3 w-12 h-1 bg-[#005BBB] rounded-full" />

            <div className="mt-8 space-y-5">
              <ContactInfo
                Icon={Phone}
                title="Téléphone"
                primary={<a href={`tel:${COMPANY.phoneTel}`} className="hover:text-[#005BBB]" data-testid="contact-tel">{COMPANY.phone}</a>}
                secondary={["Disponible 7j/7 - 24h/24", "Pour vos urgences"]}
              />
              <ContactInfo
                Icon={Mail}
                title="E-mail"
                primary={<a href={`mailto:${COMPANY.email}`} className="hover:text-[#005BBB] break-all" data-testid="contact-email">{COMPANY.email}</a>}
                secondary={["Réponse rapide par email"]}
              />
              <ContactInfo
                Icon={MapPin}
                title="Adresse"
                primary={<span>{COMPANY.street}, {COMPANY.postalCode} {COMPANY.city}</span>}
                secondary={["Intervention dans tout le Haut-Rhin"]}
              />
              <ContactInfo
                Icon={Truck}
                title="Zone d'intervention"
                primary={<span>Beblenheim • Colmar • Mulhouse • Strasbourg</span>}
                secondary={["Et alentours — déplacement gratuit"]}
              />
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-[#F5F7FA] rounded-3xl p-7 sm:p-10 shadow-sm">
              <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#005BBB] mb-3">Envoyez-nous un message</p>
              <h3 className="font-display text-2xl font-bold text-[#001B35]">Demande de devis ou question</h3>

              <form className="mt-7 space-y-5" onSubmit={onSubmit} data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field>
                    <input
                      type="text"
                      name="nom"
                      required
                      placeholder="Nom et prénom *"
                      value={form.nom}
                      onChange={onChange}
                      data-testid="contact-input-nom"
                      className="w-full px-5 py-3.5 rounded-xl bg-white border border-gray-200 focus:border-[#005BBB] focus:outline-none focus:ring-2 focus:ring-[#005BBB]/20 transition"
                    />
                  </Field>
                  <Field>
                    <input
                      type="tel"
                      name="telephone"
                      required
                      placeholder="Téléphone *"
                      value={form.telephone}
                      onChange={onChange}
                      data-testid="contact-input-tel"
                      className="w-full px-5 py-3.5 rounded-xl bg-white border border-gray-200 focus:border-[#005BBB] focus:outline-none focus:ring-2 focus:ring-[#005BBB]/20 transition"
                    />
                  </Field>
                </div>
                <Field>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="E-mail *"
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-input-email"
                    className="w-full px-5 py-3.5 rounded-xl bg-white border border-gray-200 focus:border-[#005BBB] focus:outline-none focus:ring-2 focus:ring-[#005BBB]/20 transition"
                  />
                </Field>
                <Field>
                  <select
                    name="type_demande"
                    value={form.type_demande}
                    onChange={onChange}
                    data-testid="contact-input-type"
                    className="w-full px-5 py-3.5 rounded-xl bg-white border border-gray-200 focus:border-[#005BBB] focus:outline-none focus:ring-2 focus:ring-[#005BBB]/20 transition appearance-none"
                  >
                    {REQUEST_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>
                <Field>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    placeholder="Votre message *"
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-input-message"
                    className="w-full px-5 py-3.5 rounded-xl bg-white border border-gray-200 focus:border-[#005BBB] focus:outline-none focus:ring-2 focus:ring-[#005BBB]/20 transition resize-none"
                  />
                </Field>

                <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    name="consentement"
                    checked={form.consentement}
                    onChange={onChange}
                    data-testid="contact-input-rgpd"
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-[#005BBB] focus:ring-[#005BBB]"
                  />
                  <span>
                    J'accepte que mes données soient utilisées pour me recontacter, conformément à la politique de confidentialité. *
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#005BBB] hover:bg-[#001B35] disabled:opacity-60 text-white rounded-full py-4 font-semibold uppercase tracking-wider transition btn-shine"
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  {sending ? "Envoi en cours..." : "Envoyer ma demande"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="bg-[#F5F7FA] pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-sm overflow-hidden grid lg:grid-cols-3">
            <div className="lg:col-span-1 p-8 sm:p-10 bg-[#001B35] text-white dark-grain">
              <Clock className="w-8 h-8 text-[#5dade2] mb-4" />
              <h3 className="font-display text-2xl font-bold">Besoin d'une intervention en urgence ?</h3>
              <p className="mt-3 text-[#9bb6d6] text-sm leading-relaxed">
                Nous intervenons rapidement pour tous vos dépannages en plomberie, chauffage et VMC.
              </p>
              <a href={`tel:${COMPANY.phoneTel}`} data-testid="contact-map-cta" className="mt-6 inline-flex items-center gap-2 bg-[#D90000] hover:bg-[#b00000] rounded-full px-5 py-3 font-semibold transition">
                <Phone className="w-4 h-4" /> {COMPANY.phone}
              </a>
            </div>
            <div className="lg:col-span-2 aspect-[16/10] lg:aspect-auto">
              <iframe
                title="Carte RM Plomberie"
                src={`https://www.google.com/maps?q=${COMPANY.mapsQuery}&z=12&output=embed`}
                className="w-full h-full min-h-[320px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactInfo({ Icon, title, primary, secondary = [] }) {
  return (
    <div className="flex gap-4 items-start bg-[#F5F7FA] rounded-2xl p-5">
      <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-[#005BBB]" />
      </div>
      <div className="flex-1">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#005BBB]">{title}</p>
        <p className="font-display text-lg font-bold text-[#001B35] mt-1">{primary}</p>
        {secondary.map((s) => (
          <p key={s} className="text-xs text-gray-500 mt-0.5">{s}</p>
        ))}
      </div>
    </div>
  );
}

function Field({ children }) {
  return <div className="relative">{children}</div>;
}
